import { IS_GOV_STAGING_SERVER } from './constants';
import { officeCloud } from './officeBootstrap';

/**
 * Which Elastic APM server this page reports RUM telemetry to (AB#16695).
 *
 * Government telemetry must not leave the government boundary, and the gov APM stack is
 * PRODUCTION ONLY - gov staging reports to the commercial server like any other staging env.
 * Two signals decide it:
 *
 *   officeCloud()  - whether this page is running in a government cloud at all. Read off the
 *                    office.js host actually in the tag, which nginx rewrites per web app at
 *                    container start from OFFICE_CLOUD, so it is true by construction and
 *                    never a build-time constant (AB#14694). It reads the tag's src, not
 *                    whether the script loaded, so it still answers correctly when a hardened
 *                    GCC High egress blocks the CDN - the case officeBootstrap.ts exists for.
 *
 *   IS_GOV_STAGING_SERVER  - server_url is the gov STAGING CWP (staging.gcchdev.com). The only
 *                    way to tell gov staging from gov production, which share a cloud: both
 *                    web apps are office_cloud: gcch.
 *
 * So a page in a government cloud reports to the gov server unless it is talking to the gov
 * staging CWP. Everything else reports to the commercial server, including ge-dev-gov.xml,
 * which serves the add-in from a COMMERCIAL web app (pep-prod.e-share.us) while pointing it at
 * the gov production CWP: the environment serving the page is commercial, and routing on the
 * CWP instead would make one web app report to two different APM servers depending on which
 * manifest a user happened to load.
 *
 * Note that server_url only ever moves a page towards the COMMERCIAL server, never towards the
 * gov one. A gov page whose server_url is absent - the task pane and the legacy ?theme=ge pane
 * carry none, and constants.ts' GeApiState fallback is empty until the first send - therefore
 * reports to the gov server. That is the safe direction: an unidentified gov page putting test
 * noise in the gov index is recoverable, gov user telemetry in a commercial SaaS is not.
 */

export const COMMERCIAL_APM_SERVER_URL = 'https://apm.e-share.io';
export const GOV_APM_SERVER_URL = 'https://apm.e-sharegov.us';

const isGovCloudPage = (): boolean => {
    const cloud = officeCloud();
    return cloud === 'gcc' || cloud === 'gcch' || cloud === 'dod';
};

export const apmServerUrl = (): string =>
    isGovCloudPage() && !IS_GOV_STAGING_SERVER
        ? GOV_APM_SERVER_URL
        : COMMERCIAL_APM_SERVER_URL;
